import type { SubscriptionSetupStep } from './index';
import type { Instant } from '@js-joda/core';
import type { MutationObserverResult } from '@tanstack/query-core';
import type {
    CreateSubscriptionRequest,
    CreateSubscriptionResponse,
    SetupIntentResponse,
    SubscriptionPlan,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { z } from 'zod';

export interface SubscriptionSetupState {
    currentStep: SubscriptionSetupStep;
    selectedPlanId: string | null;
    inviteCode: string;
    basePlans: z.infer<typeof SubscriptionPlan>[];
    invitePlans: z.infer<typeof SubscriptionPlan>[];
    error: string | null;
    awaitingActivation: boolean;
    cancelingSubscription: boolean;
    /**
     * Baseline `lastFailedAt` captured at the moment the user confirmed payment with Stripe.
     * `undefined` means we haven't submitted yet (no fast-polling for pending_payment_method).
     */
    knownLastFailedAt: Instant | null | undefined;
    existingPendingSubscriptionId: string | null;

    createSubscriptionResult: MutationObserverResult<
        z.infer<typeof CreateSubscriptionResponse>,
        Error,
        z.infer<typeof CreateSubscriptionRequest>,
        unknown
    > | null;
    setupIntentResult: MutationObserverResult<z.infer<typeof SetupIntentResponse>, Error, void, unknown> | null;
}

export type SubscriptionSetupAction =
    | { type: 'SET_INVITE_CODE'; inviteCode: string }
    | { type: 'SELECT_PLAN'; planId: string }
    | { type: 'CLEAR_SELECTED_PLAN' }
    | { type: 'GO_TO_STEP'; step: SubscriptionSetupStep }
    | { type: 'SET_ERROR'; error: string }
    | { type: 'CLEAR_ERROR' }
    | { type: 'FETCH_PLANS_ERROR'; error: string }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_ERROR'; error: string }
    | { type: 'CANCEL_PENDING_START' }
    | { type: 'CANCEL_PENDING_SUCCESS' }
    | { type: 'CANCEL_PENDING_ERROR'; error: string }
    | { type: 'PAYMENT_CONFIRMED'; knownLastFailedAt: Instant | null }
    | { type: 'SUBSCRIPTION_ACTIVATED' }
    | { type: 'PAYMENT_FAILED'; error: string }
    | {
          type: 'CREATE_SUBSCRIPTION_RESULT_UPDATED';
          result: SubscriptionSetupState['createSubscriptionResult'];
      }
    | {
          type: 'SETUP_INTENT_RESULT_UPDATED';
          result: SubscriptionSetupState['setupIntentResult'];
      }
    | { type: 'RESET_MUTATION_RESULTS' }
    | {
          type: 'RESET';
          pendingSubscription: { id: string; planId: string } | null;
      }
    | { type: 'SET_BASE_PLANS'; plans: z.infer<typeof SubscriptionPlan>[] }
    | { type: 'SET_INVITE_PLANS'; plans: z.infer<typeof SubscriptionPlan>[] };

export function createInitialState(): SubscriptionSetupState {
    return {
        currentStep: 'plan',
        selectedPlanId: null,
        inviteCode: '',
        basePlans: [],
        invitePlans: [],
        error: null,
        awaitingActivation: false,
        cancelingSubscription: false,
        knownLastFailedAt: undefined,
        existingPendingSubscriptionId: null,
        createSubscriptionResult: null,
        setupIntentResult: null,
    };
}

export function subscriptionSetupReducer(
    state: SubscriptionSetupState,
    action: SubscriptionSetupAction,
): SubscriptionSetupState {
    switch (action.type) {
        case 'SET_INVITE_CODE':
            return { ...state, inviteCode: action.inviteCode };

        case 'SELECT_PLAN':
            return { ...state, selectedPlanId: action.planId };

        case 'CLEAR_SELECTED_PLAN':
            return { ...state, selectedPlanId: null };

        case 'GO_TO_STEP':
            return { ...state, currentStep: action.step, error: null };

        case 'SET_ERROR':
            return { ...state, error: action.error };

        case 'CLEAR_ERROR':
            return { ...state, error: null };

        case 'FETCH_PLANS_ERROR':
            return { ...state, error: action.error };

        case 'SUBMIT_START':
            return { ...state, error: null };

        case 'SUBMIT_ERROR':
            return { ...state, error: action.error };

        case 'CANCEL_PENDING_START':
            return {
                ...state,
                createSubscriptionResult: null,
                setupIntentResult: null,
                existingPendingSubscriptionId: null,
                cancelingSubscription: true,
            };

        case 'CANCEL_PENDING_SUCCESS':
            return { ...state, cancelingSubscription: false };

        case 'CANCEL_PENDING_ERROR':
            return { ...state, cancelingSubscription: false, error: action.error };

        case 'PAYMENT_CONFIRMED':
            return {
                ...state,
                knownLastFailedAt: action.knownLastFailedAt,
                awaitingActivation: true,
            };

        case 'SUBSCRIPTION_ACTIVATED':
            return {
                ...state,
                awaitingActivation: false,
                knownLastFailedAt: undefined,
                currentStep: 'complete',
                error: null,
            };

        case 'PAYMENT_FAILED':
            return {
                ...state,
                awaitingActivation: false,
                error: action.error,
            };

        case 'CREATE_SUBSCRIPTION_RESULT_UPDATED':
            return { ...state, createSubscriptionResult: action.result };

        case 'SETUP_INTENT_RESULT_UPDATED':
            return { ...state, setupIntentResult: action.result };

        case 'RESET_MUTATION_RESULTS':
            return {
                ...state,
                createSubscriptionResult: null,
                setupIntentResult: null,
            };

        case 'RESET': {
            const initial = createInitialState();
            if (action.pendingSubscription) {
                return {
                    ...initial,
                    basePlans: state.basePlans,
                    invitePlans: state.invitePlans,
                    existingPendingSubscriptionId: action.pendingSubscription.id,
                    selectedPlanId: action.pendingSubscription.planId,
                    currentStep: 'payment',
                };
            }
            return {
                ...initial,
                basePlans: state.basePlans,
                invitePlans: state.invitePlans,
            };
        }

        case 'SET_BASE_PLANS':
            return { ...state, basePlans: action.plans };

        case 'SET_INVITE_PLANS':
            return { ...state, invitePlans: action.plans };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
